import { serialize } from "object-to-formdata";
import {
  LoginInput,
  Result,
  UserView,
  ProfileInput,
  ProfileImageInput,
  SignUpInput,
  UserRulesView,
  UserRulesInput,
  LogView,
  LogDetailsInput,
  LogAttachmentInput,
  LogAttachmentView,
  LogAttachment,
} from "../../models/general";

import { WeekScheduleInput, WeekScheduleView, UpdateCustomStatusInput, StatusHistory, LabeledFaceDescriptorsInput } from "../../models/attendance";
import request from "../../utils/request";
import { recordHourlyPayFlags } from "../../utils/hourlyPayAccess";

/**
 * In-flight coalescing for `Account/GetUserInfo`.
 *
 * WHY: this is the CO-308 endpoint — ~31s cold and the prime suspect for the
 * cold-load boot failures. Two independent consumers fire it on the SAME cold
 * load: `PrivateRoute`'s boot fetch and `revalidateUserConfig()` from
 * `MasterLayout`. Once the transient boot flags stopped being persisted (see
 * `store/redux/store/store.ts`), a returning session ran both CONCURRENTLY —
 * doubling the concurrent load on the exact endpoint we are trying to stop
 * failing. This coalesces them without coupling the call sites to each other.
 *
 * Semantics, deliberately narrow:
 *   - Only requests that overlap in time share a promise. The entry is cleared
 *     in `finally`, so the next boot (or the next retry) always re-fetches.
 *     This is NOT a response cache and has no TTL.
 *   - Every caller awaits the SAME promise and therefore gets its OWN
 *     resolution or rejection. A rejection is delivered to every awaiting
 *     caller independently — one caller's `catch` cannot swallow another's,
 *     which is what keeps PrivateRoute's boot-error panel firing.
 */
let inFlightUserInfo: Promise<Result<UserView>> | null = null;
let inFlightUserInfoStartedAt = 0;

export interface CoalescedUserInfo {
  promise: Promise<Result<UserView>>;
  /** True when this caller joined a request someone else had already started. */
  deduped: boolean;
  /**
   * `performance.now()` at which the REAL network request was kicked off — not
   * at which this caller joined. Callers that time the request must use this,
   * or a coalesced join would be recorded as a suspiciously fast success and
   * make the endpoint look like it got faster when it only got de-duplicated.
   */
  startedAt: number;
}

/**
 * Same call as `getUserInfo()`, but tells the caller whether it started the
 * request or joined one, and when the real request began. Used by the boot
 * instrumentation in `PrivateRoute`.
 */
export function getUserInfoCoalesced(): CoalescedUserInfo {
  if (inFlightUserInfo) {
    return { promise: inFlightUserInfo, deduped: true, startedAt: inFlightUserInfoStartedAt };
  }

  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const pending: Promise<Result<UserView>> = request
    .get<Result<UserView>>("Account/GetUserInfo")
    .then((response) => {
      // Hourly pay §7.2 — every GetUserInfo response passes here, so this is the one place the
      // payBasis / hourlyPayEnabled flags are recorded (utils/hourlyPayAccess.ts). Never throws.
      recordHourlyPayFlags(response.data?.payload);
      return response.data;
    })
    .finally(() => {
      // Guard the identity check so a stale settle can never clear a NEWER
      // in-flight request (e.g. a retry started after this one was abandoned).
      if (inFlightUserInfo === pending) {
        inFlightUserInfo = null;
      }
    });

  inFlightUserInfo = pending;
  inFlightUserInfoStartedAt = startedAt;
  return { promise: pending, deduped: false, startedAt };
}

export async function getUserInfo() {
  return getUserInfoCoalesced().promise;
}

export async function getUserInfoById(userId: string) {
  const response = await request.get<Result<UserView>>(`Account/${userId}`);
  return response.data;
}

export async function updateProfile(input: ProfileInput, id?: string) {
  // CO-248: the server now treats a field that was NOT posted as "leave the stored value alone"
  // (it used to overwrite it with null/0, wiping timezone, managers, IBAN and every KSA/UAE
  // statutory identifier on any partial payload). The profile screen must therefore keep doing a
  // FULL ROUND-TRIP — same convention as the CO-245 payroll editors — because under the new
  // contract "absent" no longer means "clear".
  //
  // Two things object-to-formdata would otherwise drop from the body:
  //   • `undefined` members. The screen sets a cleared date / select back to `undefined`
  //     (`d ?? undefined`), so a clear would be dropped and silently become a no-op. Sending null
  //     instead puts the key on the wire with an empty value, which the server reads as an
  //     explicit clear — identical to today's observable behaviour.
  //   • `managerIds`. `indices` is NOT cosmetic and must not be removed — it is the fix for CO-252.
  //     object-to-formdata's default emits the jQuery/PHP spelling `managerIds[]` once per element,
  //     a key ASP.NET Core's CollectionModelBinder never reads (it looks for `managerIds`, then
  //     `managerIds[0]`, `managerIds[1]`, …). So the reporting line NEVER arrived from this screen,
  //     the server saw an empty list, and every profile save archived every one of the employee's
  //     managers. `indices: true` emits `managerIds[0]` so the populated case actually binds;
  //     `allowEmptyArrays` keeps the key present when the picker is empty so "remove every manager"
  //     still clears.
  //
  // CO-248 follow-up: `managerIds` is CARVED OUT of that undefined→null coercion. For every other
  // key an explicit null is harmless — the server reads it as "clear this one field". For the
  // reporting line it is not: null puts the key on the wire, the server sees WasSent(managerIds)
  // and applies an EMPTY list, which archives every one of the employee's managers. That is CO-252
  // arriving by a second mechanism. Today it cannot fire because GetUser always projects a
  // managerIds array and the profile screen maps it with `?? []`, but "safe only because an
  // upstream mapper happens to be total" is not a contract. An undefined reporting line means
  // "leave it alone", and under preserve-on-omit that is spelled by NOT sending the key at all.
  const payload: Record<string, unknown> = { ...(input as unknown as Record<string, unknown>) };
  if (payload.managerIds === undefined) delete payload.managerIds;
  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined) payload[key] = null;
  }

  const response = await request
    .post<Result<UserView>>(id ? `account/${id}` : "account", serialize(payload, { indices: true, allowEmptyArrays: true }));
  return response.data;
}

export async function updateProfileImage(input: ProfileImageInput, id?: string) {
  const response = await request
    .post<Result<UserView>>(id ? `account/UpdateProfileImage/${id}` : "account", serialize(input));
  return response.data;
}


export async function changeLocale(locale: string) {
  const response = await request.post<Result<boolean>>(`account/changeLocale/${locale}`);
  return response.data;
}

export async function getLocale() {
  const response = await request.get<Result<string>>("account/locale");
  return response.data;
}

export async function removeImage() {
  const response = await request.delete<Result<boolean>>("account/image");
  return response.data;
}

// Unified users fetch
// scope: Company|Department|Country|Branch (case sensitive to match enum on backend)
/**
 * Exit-date lifecycle filter (docs/exit-date-lifecycle-spec.md §7).
 * - `current` (SERVER DEFAULT) — Active + Leaving. Everyone who still works here.
 * - `leaving`  — an exit date that has not passed yet.
 * - `former`   — Left: exit date passed (or an exit status with no date), not archived.
 * - `all`      — no lifecycle filter; for HR screens that segment the list themselves.
 */
export type UsersLifecycle = 'current' | 'leaving' | 'former' | 'all';

/**
 * 🔴 Omitting `lifecycle` no longer means "everyone". The server defaults to `current`, so every
 * picker built on this helper stopped offering people who have left the company without a change
 * here. Pass an explicit value only when a screen genuinely needs Former or All.
 */
export async function FetchUsers(scope: string, id: number, archived: boolean = false, lifecycle?: UsersLifecycle) {
  const response = await request.get<Result<UserView[]>>(`account/Users`, { params: { scope, id, archived, ...(lifecycle ? { lifecycle } : {}) } });
  return response.data;
}

export async function getUser(id: string) {
  const response = await request.get<Result<UserView>>(`account/${id}`);
  return response.data;
}

export async function addUsers(users: SignUpInput[]) {
  const response = await request.post<Result<string[]>>("account/addUsers", users);
  return response.data;
}

// Admin password reset — mirrors AdminResetPasswordInput / AdminResetPasswordResult on the server.
export interface AdminResetPasswordInput {
  userId: string;
  newPassword?: string;          // omit/null/empty → server auto-generates
  mustChangeOnNextLogin: boolean;
  revokeActiveSessions: boolean;
}

export interface AdminResetPasswordResult {
  generatedPassword: string;
  mustChangeOnNextLogin: boolean;
  revokedSessionCount: number;
}

export async function resetUserPassword(input: AdminResetPasswordInput) {
  const response = await request.post<Result<AdminResetPasswordResult>>(
    "account/ResetUserPassword",
    input,
  );
  return response.data;
}

// Convenience wrapper: create a single user (still backed by the existing AddUsers endpoint).
export async function addUser(user: SignUpInput) {
  return addUsers([user]);
}

// Welcome-email management — mirrors WelcomeEmailStatus / SendWelcomeEmails on the server.
// Always scoped to the caller's own company; the server ignores any companyId. (Operator is not
// admitted to this endpoint — no company employee data; see CLAUDE.md Role Model.)
// CO-663: the server returns ONLY employees who have NEVER signed in. Same DTO, fewer rows — do not
// re-derive a company-wide roster from this call, and do not count on it for "who got a welcome
// email", because anyone who has since signed in is gone from the payload by design.
export async function getWelcomeEmailStatus() {
  const response = await request.get<Result<UserView[]>>("account/WelcomeEmailStatus");
  return response.data;
}

// Per-user outcome for a welcome-email send. One bad id never aborts the rest — inspect each entry.
export interface SendWelcomeEmailResult {
  userId: string;
  success: boolean;
  message: string;
}

// Body is a RAW JSON array of user-id strings (not wrapped in an object).
export async function sendWelcomeEmails(userIds: string[]) {
  const response = await request.post<Result<SendWelcomeEmailResult[]>>(
    "account/SendWelcomeEmails",
    userIds,
  );
  return response.data;
}

export async function deleteUser(id: string) {
  const response = await request.delete<Result<{}>>(`account/${id}`, {
    params: { deleteMyAccount: false },
  });
  return response.data;
}

export async function restoreUser(id: string) {
  const response = await request.put<Result<{}>>(`account/${id}`);
  return response.data;
}

export async function getRoles() {
  const response = await request.get<Result<string[]>>(`account/roles`);
  return response.data;
}

export async function getUserManagers(userId: string) {
  const response = await request.get<Result<UserView[]>>(`account/GetUserManagers/${userId}`);
  return response.data;
}

export async function checkIsManager(userId: string) {
  const response = await request.get<Result<boolean>>(`account/CheckIsManager/${userId}`);
  return response.data;
}

export async function updateManagers(userId: string, managerIds: string[]) {
  const response = await request.post<Result<UserView>>(`account/UpdateManagers/${userId}`, {
    managerIds,
  });
  return response.data;
}

export async function getDictionary(locale: string) {
  const response = await request.get<Result<string[]>>(`account/dictionary/${locale}`);
  return response.data;
}

export async function UpdateCustomStatus(input: UpdateCustomStatusInput) {
  const response = await request.post<Result<UserView>>(`account/UpdateCustomStatus`, input);
  return response.data;
}

export async function getUserRules(userId: string) {
  const response = await request.get<Result<UserRulesView>>(`account/GetUserRules/${userId}`);
  return response.data;
}

export async function updateUserRules(userId: string, input: UserRulesInput) {
  const response = await request.post<Result<UserRulesView>>(`account/UpdateUserRules/${userId}`, input);
  return response.data;
}

export async function GetStatusHistory() {
  const response = await request.get<Result<StatusHistory[]>>(`account/GetStatusHistory`);
  return response.data;
}

export async function Log(details: LogDetailsInput) {
  // const response = await request.post<Result<Logview>>(`account/Log`,details);
  // return response.data;
  const formData = new FormData();
  formData.append('message', details.message.toString())
  formData.append('logType', details.logType.toString())
  for (let i = 0; i < details.attachments.length; i++)
    formData.append('attachments', details.attachments[i])
  const response = await request.post<Result<LogView>>(`account/Log`,
    formData);
  return response.data;

}

export async function AddLogAttachment(details: LogAttachmentInput) {
  // const response = await request.post<Result<LogAttachmentView[]>>(`account/AddLogAttachment`,details);
  // return response.data;
  const formData = new FormData();
  formData.append('logId', details.logId.toString())
  for (let i = 0; i < details.attachments.length; i++)
    formData.append('attachments', details.attachments[i])
  const response = await request.post<Result<LogAttachmentView[]>>(`account/AddLogAttachment`,
    formData);
  return response.data;
}

export async function setWeekSchedule(details: WeekScheduleInput) {
  const response = await request.post<Result<WeekScheduleView>>(`account/SetWeekSchedule`, details);
  return response.data;
}

export async function getWeekSchedule(userId: string) {
  const response = await request.get<Result<WeekScheduleView>>(`account/GetWeekSchedule/${userId}`);
  return response.data;
}

export async function getTimeSheetWeekSchedule(userId: string) {
  const response = await request.get<Result<WeekScheduleView>>(`account/GetTimeSheetWeekSchedule/${userId}`);
  return response.data;
}

export async function addLabeledFaceDescriptors(input: LabeledFaceDescriptorsInput) {
  const response = await request.post<Result<UserView>>(`account/AddLabeledFaceDescriptors`, input);
  return response.data;
}

export async function checkReEnrollmentRequired() {
  const response = await request.get<Result<{ requiresReEnrollment: boolean; hasDescriptor: boolean }>>('account/CheckReEnrollmentRequired');
  return response.data;
}

// Browser web-push (FCM). Store this browser's FCM web token so backend push
// delivery can reach it; pass an empty string to clear it (e.g. on sign-out).
// Uses the shared authenticated request client — no manual auth handling.
export async function updateWebPushToken(token: string) {
  const response = await request.post<Result<boolean>>(
    "Account/UpdateWebPushToken",
    { token },
  );
  return response.data;
}

export async function clearReEnrollmentFlag() {
  const response = await request.post<Result<boolean>>('account/ClearReEnrollmentFlag');
  return response.data;
}